
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Target, Calendar, Users, Shield, CheckCircle, AlertCircle, TrendingUp, BookOpen, Brain, Lock, Activity, Layers } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';

interface GouvernanceAgentIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SectionTitle = ({ children, icon }: { children: React.ReactNode, icon: React.ReactNode }) => (
  <h3 className="text-xl font-semibold text-indigo-300 mb-4 flex items-center">
    {icon}
    <span className="ml-2">{children}</span>
  </h3>
);

const InfoCard = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
    <div className={`bg-slate-800/50 p-4 rounded-lg border border-slate-700 ${className}`}>
        {children}
    </div>
);

const PrincipleCard = ({ title, children }: { title: string, children: React.ReactNode }) => (
  <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-700 hover:border-indigo-500 transition-all duration-300">
    <h4 className="font-bold text-indigo-300">{title}</h4>
    <p className="text-sm text-slate-300 mt-1">{children}</p>
  </div>
);


const RACI_STYLES: { [key: string]: string } = {
  R: 'bg-blue-500 text-white',
  A: 'bg-green-500 text-white',
  C: 'bg-yellow-500 text-black',
  I: 'bg-purple-500 text-white',
};

const RACICell = ({ type }: { type: string | string[] }) => {
  const types = Array.isArray(type) ? type : [type];
  return (
    <td className="p-2 text-center">
      <div className="flex justify-center items-center gap-1">
        {types.map(t => (
          <span key={t} className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${RACI_STYLES[t] || 'bg-gray-600'}`}>
            {t}
          </span>
        ))}
      </div>
    </td>
  );
};

export default function GouvernanceAgentIAModal({ isOpen, onClose }: GouvernanceAgentIAModalProps) {
  const [activeTab, setActiveTab] = useState('esprit');

  const tabs = [
    { id: 'esprit', label: 'Esprit', icon: <Eye size={16} /> },
    { id: 'cadre', label: 'Cadre', icon: <BookOpen size={16} /> },
    { id: 'architecture', label: 'Architecture', icon: <Layers size={16} /> },
    { id: 'securite', label: 'Sécurité', icon: <Shield size={16} /> },
    { id: 'raci', label: 'RACI', icon: <Users size={16} /> },
    { id: 'standards', label: 'Standards', icon: <CheckCircle size={16} /> },
    { id: 'maturite', label: 'Maturité', icon: <TrendingUp size={16} /> },
    { id: 'roadmap', label: 'Roadmap', icon: <Calendar size={16} /> },
    { id: 'kpi', label: 'KPI & ROI', icon: <Target size={16} /> },
  ];

  const maturityData = [
    { subject: 'Gouvernance', A: 20, B: 60, fullMark: 100 },
    { subject: 'Sécurité', A: 30, B: 70, fullMark: 100 },
    { subject: 'Traçabilité', A: 15, B: 65, fullMark: 100 },
    { subject: 'Ownership', A: 25, B: 80, fullMark: 100 },
    { subject: 'KPI/Mesure', A: 10, B: 55, fullMark: 100 },
    { subject: 'Architecture', A: 35, B: 75, fullMark: 100 },
    { subject: 'Processus', A: 20, B: 60, fullMark: 100 },
  ];
  
  const raciData = {
    roles: ["Direction", "Resp. Gouvernance", "Owner d'agent", "Resp. Sécurité", "Resp. Plateforme", "Chargé de projet", "Utilisateur Métier", "Comité Agentique"],
    activities: [
      { name: "Demande de création", values: ["C", "A", "R", "C", "I", "R", "R", "A"] },
      { name: "Cadrage", values: ["I", "A", "R", "C", "I", "R", "C", "A"] },
      { name: "Approbation", values: ["A", "A", "C", "A", "I", "I", "I", "R"] },
      { name: "Accès systèmes", values: ["I", "C", "R", "A", "R", "I", "I", "C"] },
      { name: "Mise en production", values: ["I", "A", "A", "R", "R", "R", "I", "A"] },
      { name: "Suivi performance", values: ["C", "C", "R", "I", "I", "A", "I", "I"] },
      { name: "Gestion d'incident", values: ["I", "A", "R", "R", "C", "C", "I", "A"] },
      { name: "Audit", values: ["A", "R", "C", "A", "C", "I", "I", "R"] },
      { name: "Retrait d'un agent", values: ["A", "A", "R", "C", "C", "R", "I", "A"] },
    ]
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'esprit':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Brain size={20} />}>Esprit & Objectifs Macro</SectionTitle>
            <InfoCard className="mb-6">
              <h4 className="font-bold text-lg text-white mb-2">Synthèse Exécutive</h4>
              <p className="text-slate-300">BZ met en place une gouvernance formelle pour ses agents IA afin de structurer leur développement, sécuriser leur intégration et maximiser leur valeur ajoutée. Ce cadre vise à transformer l'expérimentation en une capacité organisationnelle robuste, contrôlée et alignée avec les objectifs stratégiques.</p>
            </InfoCard>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
                <InfoCard>
                    <h4 className="font-semibold text-green-400 mb-2">Objectifs d'Affaires</h4>
                    <ul className="list-disc list-inside text-slate-300 text-sm">
                        <li>Augmenter la productivité</li>
                        <li>Améliorer la qualité des suivis</li>
                        <li>Accélérer la prise de décision</li>
                        <li>Standardiser les processus</li>
                    </ul>
                </InfoCard>
                <InfoCard>
                    <h4 className="font-semibold text-blue-400 mb-2">Bénéfices Attendus</h4>
                    <ul className="list-disc list-inside text-slate-300 text-sm">
                        <li>ROI mesurable sur l'IA</li>
                        <li>Adoption durable et sécurisée</li>
                        <li>Réduction des tâches répétitives</li>
                        <li>Amélioration de la conformité</li>
                    </ul>
                </InfoCard>
                <InfoCard>
                    <h4 className="font-semibold text-red-400 mb-2">Risques Évités</h4>
                    <ul className="list-disc list-inside text-slate-300 text-sm">
                        <li>Agents non gouvernés ("shadow AI")</li>
                        <li>Accès incontrôlés aux données</li>
                        <li>Actions irréversibles non validées</li>
                        <li>Dépendance à des solutions opaques</li>
                    </ul>
                </InfoCard>
            </div>
             <div className="border border-yellow-500/50 rounded-lg p-4 bg-yellow-900/10 mb-6">
                <h4 className="font-bold text-yellow-400 mb-3 text-lg">Principes Non Négociables</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <PrincipleCard title="Human in the loop">Supervision humaine obligatoire pour les actions critiques.</PrincipleCard>
                    <PrincipleCard title="Deny by default">Le moindre privilège est la règle pour les accès et actions.</PrincipleCard>
                    <PrincipleCard title="Security by design">La sécurité est intégrée à chaque étape du cycle de vie.</PrincipleCard>
                    <PrincipleCard title="Traçabilité by design">Chaque décision et action de l'agent est journalisée.</PrincipleCard>
                    <PrincipleCard title="Gouvernance avant automatisation">Aucune automatisation sans un cadre de contrôle clair.</PrincipleCard>
                    <PrincipleCard title="ROI réel avant multiplication">La valeur doit être prouvée avant de passer à l'échelle.</PrincipleCard>
                </div>
            </div>
          </motion.div>
        );
      case 'cadre':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<BookOpen size={20} />}>Cadre de Gouvernance</SectionTitle>
            <div className="grid md:grid-cols-2 gap-6">
                <div>
                    <InfoCard className="mb-6">
                      <h4 className="font-bold text-lg text-white mb-2">Mission & Principes</h4>
                      <p className="text-slate-300 mb-3">Assurer que chaque agent IA déployé est sécurisé, conforme, aligné aux objectifs de BZ et crée une valeur mesurable.</p>
                      <ul className="list-disc list-inside text-indigo-300 text-sm space-y-1">
                        <li>Alignement stratégique</li>
                        <li>Propriété claire (Ownership)</li>
                        <li>Gestion des risques proactive</li>
                        <li>Transparence et traçabilité</li>
                        <li>Amélioration continue</li>
                      </ul>
                    </InfoCard>
                     <div className="p-4 rounded-lg bg-red-900/20 border border-red-500/50">
                        <h4 className="font-bold text-red-400 mb-3 text-lg flex items-center"><AlertCircle className="mr-2" />Règles Minimales (Mise en production)</h4>
                        <ul className="text-slate-300 text-sm space-y-2">
                           <li><span className="font-semibold">Owner humain</span>: Aucun agent sans propriétaire imputable.</li>
                           <li><span className="font-semibold">Périmètre défini</span>: Responsabilités et limites clairement documentées.</li>
                           <li><span className="font-semibold">Journalisation</span>: Toutes les actions sont tracées et auditables.</li>
                           <li><span className="font-semibold">Politique d'accès</span>: Droits d'accès basés sur le moindre privilège.</li>
                           <li><span className="font-semibold">Plan de supervision</span>: Mécanismes de surveillance actifs.</li>
                           <li><span className="font-semibold">Escalade humaine</span>: Procédure claire pour les agents critiques.</li>
                        </ul>
                    </div>
                </div>
                <div>
                   <InfoCard>
                        <h4 className="font-bold text-lg text-white mb-3">Cycle de Vie d'un Agent</h4>
                        <ol className="relative border-l border-indigo-700">                  
                            <li className="mb-6 ml-4">
                                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full mt-1.5 -left-1.5 border-2 border-slate-900"></div>
                                <h3 className="text-base font-semibold text-white">Besoin d'affaires → Cadrage → Analyse de risque</h3>
                            </li>
                            <li className="mb-6 ml-4">
                                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full mt-1.5 -left-1.5 border-2 border-slate-900"></div>
                                <h3 className="text-base font-semibold text-white">Architecture cible → Approbation</h3>
                            </li>
                            <li className="mb-6 ml-4">
                                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full mt-1.5 -left-1.5 border-2 border-slate-900"></div>
                                <h3 className="text-base font-semibold text-white">Configuration → Tests → Mise en service</h3>
                            </li>
                             <li className="mb-6 ml-4">
                                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full mt-1.5 -left-1.5 border-2 border-slate-900"></div>
                                <h3 className="text-base font-semibold text-white">Supervision → Amélioration continue</h3>
                            </li>
                            <li className="ml-4">
                                <div className="absolute w-3 h-3 bg-indigo-500 rounded-full mt-1.5 -left-1.5 border-2 border-slate-900"></div>
                                <h3 className="text-base font-semibold text-white">Retrait / Désactivation</h3>
                            </li>
                        </ol>
                   </InfoCard>
                </div>
            </div>
          </motion.div>
        );
       case 'architecture':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Layers size={20} />}>Architecture Logique</SectionTitle>
             <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/2 space-y-2">
                    <div className="text-center p-3 rounded-lg bg-red-800/70 border border-red-600">
                        <h4 className="font-bold text-white">1. Objectifs / Besoins d'affaires</h4>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-yellow-800/70 border border-yellow-600">
                        <h4 className="font-bold text-white">2. Gouvernance / Politiques / Standards</h4>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-blue-800/70 border border-blue-600">
                        <h4 className="font-bold text-white">3. Orchestration agentique</h4>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-indigo-800/70 border border-indigo-600">
                        <h4 className="font-bold text-white">4. Outils / Systèmes connectés</h4>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-purple-800/70 border border-purple-600">
                        <h4 className="font-bold text-white">5. Données / Mémoire / Backlog / Journaux</h4>
                    </div>
                     <div className="text-center p-3 rounded-lg bg-green-800/70 border border-green-600">
                        <h4 className="font-bold text-white">6. Supervision / Audit / Contrôle humain</h4>
                    </div>
                </div>
                <div className="w-full md:w-1/2">
                  <InfoCard>
                    <h4 className="font-bold text-lg text-white mb-2">Principes d'Architecture</h4>
                    <p className="text-slate-300 mb-4 text-sm">L'architecture est conçue pour garantir un contrôle maximal à chaque couche, en appliquant des principes de sécurité et de gouvernance stricts.</p>
                     <ul className="text-slate-300 text-sm space-y-2">
                        <li className="flex items-start"><Lock size={14} className="mr-2 mt-1 text-indigo-400 flex-shrink-0" /><div><span className="font-semibold">Deny by default & Moindre privilège :</span> Accès minimaux par défaut.</div></li>
                        <li className="flex items-start"><Users size={14} className="mr-2 mt-1 text-indigo-400 flex-shrink-0" /><div><span className="font-semibold">Séparation des rôles :</span> Fonctions et accès cloisonnés.</div></li>
                        <li className="flex items-start"><Activity size={14} className="mr-2 mt-1 text-indigo-400 flex-shrink-0" /><div><span className="font-semibold">Journalisation intégrale :</span> Tout est tracé pour l'audit.</div></li>
                        <li className="flex items-start"><Eye size={14} className="mr-2 mt-1 text-indigo-400 flex-shrink-0" /><div><span className="font-semibold">Validation humaine :</span> Points de contrôle obligatoires selon la criticité.</div></li>
                        <li className="flex items-start"><Layers size={14} className="mr-2 mt-1 text-indigo-400 flex-shrink-0" /><div><span className="font-semibold">Segmentation & Protection :</span> Outils et secrets sont isolés et protégés.</div></li>
                     </ul>
                  </InfoCard>
                </div>
             </div>
          </motion.div>
        );
      case 'securite':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Shield size={20} />}>Sécurité, Risque & Conformité</SectionTitle>
             <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 text-center">
                 <InfoCard><h4 className="font-semibold text-indigo-300">Identifier</h4><p className="text-xs text-slate-400">Classifier les agents et les données</p></InfoCard>
                 <InfoCard><h4 className="font-semibold text-indigo-300">Protect</h4><p className="text-xs text-slate-400">Contrôler les accès et les permissions</p></InfoCard>
                 <InfoCard><h4 className="font-semibold text-indigo-300">Detect</h4><p className="text-xs text-slate-400">Surveiller les comportements anormaux</p></InfoCard>
                 <InfoCard><h4 className="font-semibold text-indigo-300">Respond</h4><p className="text-xs text-slate-400">Plan de désactivation rapide</p></InfoCard>
                 <InfoCard><h4 className="font-semibold text-indigo-300">Recover</h4><p className="text-xs text-slate-400">Conserver les traces et analyser</p></InfoCard>
             </div>
             <InfoCard>
                <h4 className="font-bold text-lg text-white mb-3">Matrice de Risque</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-300">
                        <thead className="text-xs text-indigo-300 uppercase bg-slate-800">
                            <tr>
                                <th scope="col" className="px-6 py-3">Niveau</th>
                                <th scope="col" className="px-6 py-3">Critères</th>
                                <th scope="col" className="px-6 py-3">Contrôle Requis</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-700 bg-green-900/20">
                                <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">Faible</th>
                                <td className="px-6 py-4">Accès limité, données non sensibles, supervision optionnelle</td>
                                <td className="px-6 py-4">Journalisation basique, revue trimestrielle</td>
                            </tr>
                            <tr className="border-b border-slate-700 bg-yellow-900/20">
                                <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">Modéré</th>
                                <td className="px-6 py-4">Accès systèmes internes, données structurées, impact opérationnel</td>
                                <td className="px-6 py-4">Journalisation complète, revue mensuelle, owner nommé</td>
                            </tr>
                            <tr className="bg-red-900/20">
                                <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">Élevé</th>
                                <td className="px-6 py-4">Accès données sensibles, actions irréversibles, impact critique</td>
                                <td className="px-6 py-4">Validation humaine obligatoire, audit continu, escalade définie</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
             </InfoCard>
          </motion.div>
        );
      case 'raci':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Users size={20} />}>RACI & Rôles</SectionTitle>
             <InfoCard>
                <h4 className="font-bold text-lg text-white mb-3">Matrice RACI</h4>
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                    <tr className="text-left text-xs text-indigo-300 uppercase bg-slate-800">
                        <th className="p-2">Activité</th>
                        {raciData.roles.map(role => <th key={role} className="p-2 text-center text-[10px] transform -rotate-45 h-20 leading-5">{role}</th>)}
                    </tr>
                    </thead>
                    <tbody className="text-slate-300">
                    {raciData.activities.map(activity => (
                        <tr key={activity.name} className="border-b border-slate-700">
                        <td className="p-2 font-semibold">{activity.name}</td>
                        {activity.values.map((val, index) => <RACICell key={index} type={val} />)}
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </InfoCard>
          </motion.div>
        );
      case 'standards':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<CheckCircle size={20} />}>Standards Opérationnels Minimaux</SectionTitle>
            <InfoCard>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                        "Mandat documenté", "Owner nommé", "Objectifs et KPI définis",
                        "Backlog maintenu", "Règles d'escalade définies", "Accès approuvés",
                        "Journaux activés", "Historique des changements", "Documentation PRIsME à jour",
                        "Procédure de désactivation", "Validation humaine (si critique)", "Stratégie de gestion des erreurs",
                        "Définition du périmètre", "Critères de succès", "Mécanisme de revue périodique"
                    ].map(item => (
                        <div key={item} className="flex items-center bg-slate-800 p-3 rounded-lg">
                            <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                            <span className="text-slate-200">{item}</span>
                        </div>
                    ))}
                 </div>
            </InfoCard>
          </motion.div>
        );
      case 'maturite':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<TrendingUp size={20} />}>Maturité</SectionTitle>
            <div className="grid md:grid-cols-2 gap-6">
                <InfoCard>
                    <h4 className="font-bold text-lg text-white mb-2">Niveaux de Maturité</h4>
                    <ol className="list-decimal list-inside text-slate-300 space-y-2 text-sm">
                        <li><span className="font-semibold text-red-400">Expérimental / Artisanal:</span> Agents non structurés, sans supervision formelle.</li>
                        <li><span className="font-semibold text-yellow-400">Encadré partiellement:</span> Premiers standards appliqués, ownership défini.</li>
                        <li><span className="font-semibold text-green-400">Gouverné (Cible):</span> Cadre de gouvernance complet, sécurité et traçabilité assurées.</li>
                        <li><span className="font-semibold text-blue-400">Intégré et mesuré:</span> KPI et ROI suivis, intégration forte aux processus.</li>
                        <li><span className="font-semibold text-indigo-400">Optimisé et scalable:</span> Amélioration continue, déploiement industrialisé.</li>
                    </ol>
                </InfoCard>
                <InfoCard>
                   <h4 className="font-bold text-lg text-white mb-2 text-center">Actuel vs. Cible</h4>
                   <ResponsiveContainer width="100%" height={250}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={maturityData}>
                        <PolarGrid stroke="#4A5568" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: '#A0AEC0', fontSize: 12 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: 'none' }} axisLine={{ stroke: 'none' }} />
                        <Radar name="Actuel (Niveau 1)" dataKey="A" stroke="#F56565" fill="#F56565" fillOpacity={0.6} />
                        <Radar name="Cible (Niveau 3)" dataKey="B" stroke="#48BB78" fill="#48BB78" fillOpacity={0.6} />
                        <Legend wrapperStyle={{ fontSize: "12px" }} />
                    </RadarChart>
                   </ResponsiveContainer>
                </InfoCard>
            </div>
          </motion.div>
        );
       case 'roadmap':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Calendar size={20} />}>Roadmap</SectionTitle>
            <div className="space-y-4">
              {[
                { phase: 1, title: 'Cadrage et standards de base', deliverables: "Principes, cycle de vie, RACI initial" },
                { phase: 2, title: 'Architecture, sécurité, accès, journalisation', deliverables: "Architecture cible, matrice de risque, politique d'accès" },
                { phase: 3, title: 'Premiers agents gouvernés', deliverables: "Déploiement de 2-3 agents pilotes sous le nouveau cadre" },
                { phase: 4, title: 'Intégration complète backlog / ROI / audit', deliverables: "Tableau de bord de suivi KPI/ROI, processus d'audit" },
                { phase: 5, title: 'Optimisation continue et industrialisation', deliverables: "Playbook de déploiement, revue périodique du cadre" },
              ].map(p => (
                <InfoCard key={p.phase}>
                    <h4 className="font-bold text-lg text-indigo-300">Phase {p.phase}: {p.title}</h4>
                    <p className="text-sm text-slate-300 mt-1"><span className="font-semibold text-slate-400">Livrables:</span> {p.deliverables}</p>
                </InfoCard>
              ))}
            </div>
          </motion.div>
        );
      case 'kpi':
        return (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle icon={<Target size={20} />}>KPI, Traçabilité & ROI</SectionTitle>
             <div className="grid md:grid-cols-2 gap-6">
                <InfoCard>
                    <h4 className="font-bold text-lg text-white mb-3">Indicateurs Clés de Performance (KPI)</h4>
                     <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-300">
                        <li># agents actifs gouvernés</li>
                        <li>% agents documentés</li>
                        <li>% agents avec owner</li>
                        <li>% actions tracées</li>
                        <li>Délai moyen de traitement</li>
                        <li># incidents / escalades</li>
                        <li>Temps économisé (estimé)</li>
                        <li>Qualité des suivis (score)</li>
                        <li>Taux d'escalade humaine</li>
                        <li>ROI estimé / observé</li>
                    </ul>
                </InfoCard>
                <InfoCard>
                    <h4 className="font-bold text-lg text-white mb-3">Stratégie de Traçabilité</h4>
                    <dl className="text-sm text-slate-300 space-y-2">
                        <div>
                            <dt className="font-semibold text-indigo-300">Quoi tracer ?</dt>
                            <dd>Décisions, actions, accès aux données, erreurs, interventions humaines.</dd>
                        </div>
                        <div>
                            <dt className="font-semibold text-indigo-300">Pourquoi ?</dt>
                            <dd>Pour l'audit, la sécurité, le débogage et l'amélioration continue.</dd>
                        </div>
                        <div>
                            <dt className="font-semibold text-indigo-300">Où tracer ?</dt>
                            <dd>Journaux centralisés, versionnés et immuables.</dd>
                        </div>
                    </dl>
                </InfoCard>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col border border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
            <h2 className="text-xl font-bold text-white flex items-center">
              <Layers className="mr-3 text-indigo-400" />
              Gouvernance Agent IA
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </header>

          <nav className="flex-shrink-0 border-b border-slate-700 px-2">
             <div className="flex space-x-1 overflow-x-auto">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200 ease-in-out focus:outline-none whitespace-nowrap ${
                        activeTab === tab.id
                            ? 'border-b-2 border-indigo-400 text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        {tab.icon}
                        <span className="ml-2">{tab.label}</span>
                    </button>
                ))}
            </div>
          </nav>

          <main className="p-6 overflow-y-auto flex-grow">
            {renderContent()}
          </main>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
